"use client";

import { Fragment, useEffect, useRef, useState } from "react";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { stegaClean } from "@sanity/client/stega";
import { ImageElement } from "../Image";
import { useAppContext } from "../../provider/AppProvider";
import { HeroPeopleProps } from "./heroPeople";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export const HeroPeopleDesktop = ({ title, people }: HeroPeopleProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { setCursorState } = useAppContext();
    const [dotPosition, setDotPosition] = useState<number>(0);
    const [dotVisible, setDotVisible] = useState<boolean>(true);

    const [activePersonIndex, setActivePersonIndex] = useState<number | null>(0);

    const cleanTitle = stegaClean(title);

    const personWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const dot = document.querySelector(".dot") as HTMLElement;
        const people = document.querySelectorAll(".name") as NodeListOf<HTMLElement>;
        setDotPosition(dot.getBoundingClientRect().top);

        // Check if which person is at same height as the dot
        const checkDotPosition = () => {
            const dotRect = dot.getBoundingClientRect();
            const dotTop = dotRect.top + window.scrollY;

            people.forEach((person, index) => {
                const personRect = person.getBoundingClientRect();
                const personTop = personRect.top + window.scrollY;

                if (dotTop >= personTop && dotTop < personTop + personRect.height - 40) {
                    setActivePersonIndex(index);

                    const factor = document.querySelector(`.fact-${index}`) as HTMLElement;
                    factor.classList.add("active");

                    setDotVisible(true);
                    setCursorState("hidden");
                }
            });
        };

        // Initial check
        checkDotPosition();
        // Add scroll event listener
        window.addEventListener("scroll", checkDotPosition);
        // Cleanup function to remove the event listener
        return () => {
            window.removeEventListener("scroll", checkDotPosition);
        };
    }, []);

    useGSAP(
        () => {
            if (dotPosition === 0) return;

            ScrollTrigger.create({
                trigger: ".right-column",
                start: "top 90px",
                end: "bottom bottom",
                pin: ".left-column",
                pinSpacing: false,
                invalidateOnRefresh: true,
            });

            ScrollTrigger.create({
                trigger: ".container-inner",
                start: "top top",
                end: "bottom bottom",
                pin: ".dot",
                pinSpacing: false,
                invalidateOnRefresh: true,
            });

            ScrollTrigger.refresh();

            return () => {
                ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
            };
        },
        { scope: containerRef, dependencies: [dotPosition] },
    );

    useGSAP(
        () => {
            const headline = document.querySelector(".and") as HTMLElement;

            gsap.set(headline, { y: "110%" });

            gsap.to(headline, {
                duration: 1,
                y: 0,
                stagger: 0.075,
                ease: "power4.out",
                mask: "lines",
            });
        },
        { scope: containerRef },
    );

    return (
        <div
            className="container sticky hero-people intersection-hero"
            ref={containerRef}
            onMouseEnter={() => {
                setCursorState("hidden");
                setDotVisible(true);
            }}
            onMouseLeave={() => {
                setCursorState("default");
                setDotVisible(false);
            }}
        >
            <div className="pb-20 mx-5 container-inner">
                <div className="relative grid grid-cols-12 gap-5">
                    <div className="relative flex col-span-6">
                        <div className="relative w-full wrapper max-h-[calc(100vh-150px)] left-column">
                            <div className="relative flex justify-center w-full h-full">
                                {people?.map((person, index) => (
                                    <div
                                        className={`absolute top-1/2 -translate-y-1/2 ${activePersonIndex === index ? "opacity-100" : "opacity-0"} `}
                                        key={index}
                                    >
                                        {person.image && person.image.image && (
                                            <ImageElement
                                                src={person.image?.image}
                                                width={800}
                                                alt={person.image.alt || ""}
                                                className={`relative object-cover max-h-[calc(90vh-150px)] aspect-portrait`}
                                            />
                                        )}
                                        <div
                                            className={`absolute -bottom-8 w-full fact bg-white flex overflow-hidden`}
                                        >
                                            <div
                                                className={`fact-${index} w-auto d-body-m pt-3 flex whitespace-nowrap py-1`}
                                            >
                                                <span className="px-1">{person.facts}</span>
                                                <span className="px-1">{person.facts}</span>
                                                <span className="">{person.facts}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="relative col-span-6 col-start-7 right-column">
                        <p className="mb-16 d-display-1 and">{cleanTitle}</p>

                        <div
                            className={`absolute w-12 h-12 mt-1 -ml-16 bg-black rounded-full dot transition-opacity ${dotVisible ? "opacity-100" : "opacity-0"}`}
                        ></div>

                        <div ref={personWrapperRef}>
                            {people?.map((person, index) => (
                                <Fragment key={index}>
                                    <p key={index} className="d-headline-2 xl:d-headline-1 name">
                                        {person.name}
                                    </p>
                                </Fragment>
                            ))}
                            <div
                                style={
                                    {
                                        "--dot-position": `calc(100vh - ${dotPosition + 50}px)`,
                                    } as React.CSSProperties
                                }
                                className={`h-[var(--dot-position)]`}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
